import { Controller } from 'react-hook-form';
import { InputText, InputSize } from '@ui/src';
import RiskFormWrapper from '../partial/risk-form-wrapper';
import RiskFormItem from '../partial/risk-form-item';
import FormBlock from './form-block';
import { RiskScoreCalculationType } from '../../hoc/types';
import { GeographicalRiskFormTypes } from './types';

import cn from './form.module.css';

const GeographicalRiskForm = ({
  control,
  transactionsCalculate,
  individualClientCalculate,
  corporateClientCalculate,
  onSubmit,
  requestInProgress,
  transactionsRisk,
  updateAllowed,
  weightErrorVisible,
}: GeographicalRiskFormTypes) => (
  <RiskFormWrapper
    onSubmit={onSubmit}
    requestInProgress={requestInProgress}
    disabled={!updateAllowed}
  >
    <>
      {transactionsRisk && (
        <RiskFormItem title="Factors for external transactions">
          <FormBlock
            control={control}
            radioName="transactionsCalculate"
            calculateBy={transactionsCalculate}
            disabled={!updateAllowed}
          >
            <>
              <div className={cn.formBlockInputWrapper}>
                <Controller
                  control={control}
                  name="transactionNationality"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="transactionNationality"
                      placeholder=""
                      onChange={onChange}
                      value={value}
                      title="Country of the counterparty"
                      size={InputSize.SMALL}
                      disabled={
                        !updateAllowed ||
                        transactionsCalculate !==
                          RiskScoreCalculationType.WEIGHT
                      }
                      labelClassName={cn.formBlockLabel}
                    />
                  )}
                />
              </div>
              <div className={cn.formBlockInputWrapper}>
                <Controller
                  control={control}
                  name="transactionCountryOfResidence"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="transactionCountryOfResidence"
                      placeholder=""
                      onChange={onChange}
                      value={value}
                      title="Country of the bank of the counterparty"
                      size={InputSize.SMALL}
                      disabled={
                        !updateAllowed ||
                        transactionsCalculate !==
                          RiskScoreCalculationType.WEIGHT
                      }
                      labelClassName={cn.formBlockLabel}
                    />
                  )}
                />
              </div>
              {weightErrorVisible && (
                <div className={cn.weightError}>
                  Sum of country of the counterparty and country of the bank of
                  the counterparty must be equal 1
                </div>
              )}
            </>
          </FormBlock>
        </RiskFormItem>
      )}
      {!transactionsRisk && individualClientCalculate && (
        <RiskFormItem title="Factors for individual clients">
          <FormBlock
            control={control}
            radioName="individualClientCalculate"
            calculateBy={individualClientCalculate}
            disabled={!updateAllowed}
          >
            <>
              <div className={cn.formBlockInputWrapper}>
                <Controller
                  control={control}
                  name="individualClientNationality"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="individualClientNationality"
                      placeholder=""
                      onChange={onChange}
                      value={value}
                      size={InputSize.SMALL}
                      title="Nationality"
                      disabled={
                        individualClientCalculate !==
                          RiskScoreCalculationType.WEIGHT || !updateAllowed
                      }
                      labelClassName={cn.formBlockLabel}
                    />
                  )}
                />
              </div>
              <div className={cn.formBlockInputWrapper}>
                <Controller
                  control={control}
                  name="individualClientCountryOfResidence"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="individualClientCountryOfResidence"
                      placeholder=""
                      onChange={onChange}
                      value={value}
                      size={InputSize.SMALL}
                      title="Country of residence"
                      disabled={
                        individualClientCalculate !==
                          RiskScoreCalculationType.WEIGHT || !updateAllowed
                      }
                      labelClassName={cn.formBlockLabel}
                    />
                  )}
                />
              </div>
              {weightErrorVisible && (
                <div className={cn.weightError}>
                  Sum of nationality and country of residence must be equal 1
                </div>
              )}
            </>
          </FormBlock>
        </RiskFormItem>
      )}
      {!transactionsRisk && corporateClientCalculate && (
        <RiskFormItem title="Factors for corporate clients">
          <FormBlock
            control={control}
            radioName="corporateClientCalculate"
            calculateBy={corporateClientCalculate}
            disabled={!updateAllowed}
          >
            <>
              <div className={cn.formBlockInputWrapper}>
                <Controller
                  control={control}
                  name="corporateClientCountryOfIncorporation"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div className={cn.formBlockInputTitle}>
                        <div
                          className={cn.hint}
                          data-content="Country of incorporation or business address/ correspondence address if different"
                        >
                          i
                        </div>
                        <span>Country of incorporation</span>
                      </div>
                      <InputText
                        name="corporateClientCountryOfIncorporation"
                        placeholder=""
                        onChange={onChange}
                        size={InputSize.SMALL}
                        value={value}
                        disabled={
                          corporateClientCalculate !==
                            RiskScoreCalculationType.WEIGHT || !updateAllowed
                        }
                      />
                    </>
                  )}
                />
              </div>
              <div className={cn.formBlockInputWrapper}>
                <Controller
                  control={control}
                  name="corporateClientAverageRiskScore"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div
                        className={`${cn.formBlockInputTitle} ${cn.transform}`}
                      >
                        <div
                          className={cn.hint}
                          data-content="Average risk score of all UBOs/directors"
                        >
                          i
                        </div>
                        <span>Av.risk score of all UBOs</span>
                      </div>
                      <InputText
                        name="corporateClientAverageRiskScore"
                        placeholder=""
                        onChange={onChange}
                        size={InputSize.SMALL}
                        value={value}
                        disabled={
                          corporateClientCalculate !==
                            RiskScoreCalculationType.WEIGHT || !updateAllowed
                        }
                      />
                    </>
                  )}
                />
              </div>
              {weightErrorVisible && (
                <div className={cn.weightError}>
                  Sum of country of incorporation and av.risk score of all UBOs
                  must be equal 1
                </div>
              )}
            </>
          </FormBlock>
        </RiskFormItem>
      )}
    </>
  </RiskFormWrapper>
);

export default GeographicalRiskForm;
